import React from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard } from "lucide-react";

// Same animated multi-ring spinner as the Measurement loader
// (components/EnhancedLoadingIndicator.tsx) - orbiting dots, dual rotating
// rings, pulsing/glowing center - but recolored to the app's actual
// primary/warning tokens (tailwind.config.js) instead of Measurement's
// blue/indigo/purple, and using the same heading/body type scale already
// used across the RCT Experiment screens (Card/CardHeader in ResultsStep,
// the h2 in RctExperimentStudy) instead of Measurement's larger gradient-clip
// heading.
interface RctLoadingIndicatorProps {
  title: string;
  message?: string;
  percent: number;
}

const RctLoadingIndicator: React.FC<RctLoadingIndicatorProps> = ({ title, message, percent }) => {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center justify-center min-h-[480px] px-4">
      <div className="relative mb-8">
        <div className="relative w-24 h-24">
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary border-r-primary-light animate-spin"></div>
          <div
            className="absolute inset-2 rounded-full border-4 border-transparent border-b-primary-dark border-l-primary-light animate-spin"
            style={{ animationDirection: "reverse", animationDuration: "1.5s" }}
          ></div>

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 bg-gradient-to-br from-primary via-primary-light to-primary-dark rounded-full animate-pulse shadow-lg flex items-center justify-center">
              <span className="font-mono text-[11px] font-semibold text-white">{Math.round(percent)}%</span>
            </div>
          </div>

          <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary-light to-primary-dark blur-2xl opacity-30 animate-pulse"></div>
        </div>

        <div className="absolute inset-0 animate-spin" style={{ animationDuration: "3s" }}>
          <div className="absolute top-0 left-1/2 w-3 h-3 bg-primary rounded-full shadow-lg -translate-x-1/2"></div>
        </div>
        <div className="absolute inset-0 animate-spin" style={{ animationDuration: "3s", animationDelay: "1s" }}>
          <div className="absolute top-0 left-1/2 w-3 h-3 bg-primary-light rounded-full shadow-lg -translate-x-1/2"></div>
        </div>
        <div className="absolute inset-0 animate-spin" style={{ animationDuration: "3s", animationDelay: "2s" }}>
          <div className="absolute top-0 left-1/2 w-3 h-3 bg-warning-500 rounded-full shadow-lg -translate-x-1/2"></div>
        </div>
      </div>

      <div className="max-w-lg w-full text-center">
        <h3 className="text-2xl font-semibold text-gray-900 mb-3">{title}</h3>
        {message && <p className="text-sm text-gray-600 leading-relaxed mb-2">{message}</p>}
        <p className="text-sm text-gray-500 leading-relaxed mb-1">
          Matching test/control markets and sizing power &amp; investment for every selected split, searched
          across every candidate duration — this can take tens of minutes.
        </p>
        <p className="text-xs text-gray-400">
          This runs in the background — feel free to leave this page and come back later.
        </p>
      </div>

      <button
        onClick={() => navigate("/rct-experiment/list")}
        className="mt-8 inline-flex items-center gap-2 px-8 py-2.5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-r from-primary to-primary-dark border-0 hover:brightness-110 transform hover:scale-105 text-white font-semibold text-sm"
      >
        <LayoutDashboard className="w-4 h-4" />
        Go to RCT Experiments Dashboard
      </button>
    </div>
  );
};

export default RctLoadingIndicator;
